import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { visibleSections } from "./navConfig";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Search, TrendingUp, CornerDownLeft } from "lucide-react";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

/** Type anywhere in the top bar to jump to a stock or a screen. */
export const NavSearch = () => {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const latest = useRef("");

  const pages = useMemo(() => {
    const out = [];
    visibleSections(t, user?.pro_mode).forEach((s) => s.items.forEach((it) => {
      if (it.children) it.children.forEach((c) => out.push({ ...c, section: it.label }));
      else out.push({ ...it, section: s.label });
    }));
    return out;
  }, [t, user?.pro_mode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const q = query.trim();
    latest.current = q;
    if (q.length < 2) { setHits([]); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/market/instruments", { params: { search: q } });
        if (latest.current !== q) return;
        setHits(data.slice(0, 8));
      } catch (e) {
        console.error("Search failed:", e);
      } finally { if (latest.current === q) setLoading(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [query]);

  const go = (to) => { setOpen(false); setQuery(""); navigate(to); };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="nav-search-trigger"
        className="nav-chip hidden md:inline-flex items-center gap-2 ps-3 pe-2 text-muted-foreground hover:text-foreground">
        <Search className="h-3.5 w-3.5" />
        <span className="text-xs">{t("search_placeholder")}</span>
        <kbd className="ms-1 rounded-md border border-border/70 bg-background/70 px-1.5 py-0.5 text-[10px] font-medium">
          {isMac ? "⌘" : "Ctrl"} K
        </kbd>
      </button>
      <button onClick={() => setOpen(true)} data-testid="nav-search-trigger-mobile"
        className="nav-chip md:hidden inline-flex items-center justify-center h-9 w-9 text-muted-foreground">
        <Search className="h-[18px] w-[18px]" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("search_hint")} value={query} onValueChange={setQuery}
          data-testid="nav-search-input" />
        <CommandList data-testid="nav-search-results">
          <CommandEmpty>
            {loading ? t("searching") : query.trim().length < 2 ? t("search_hint") : t("search_no_match")}
          </CommandEmpty>

          {!!hits.length && (
            <CommandGroup heading={t("search_stocks")}>
              {hits.map((s) => (
                <CommandItem key={`sym-${s.symbol}`} value={`${s.symbol} ${s.name}`}
                  onSelect={() => go(`/stock/${encodeURIComponent(s.symbol)}`)}
                  data-testid={`nav-search-symbol-${s.symbol}`}>
                  <TrendingUp className="me-2 h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{s.symbol}</span>
                  <span className="ms-2 truncate text-xs text-muted-foreground">{s.name}</span>
                  <span className="ms-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                    {s.market}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandGroup heading={t("search_pages")}>
            {pages.map((p) => (
              <CommandItem key={`page-${p.to}`} value={`${p.label} ${p.section}`}
                onSelect={() => go(p.to)} data-testid={`nav-search-page-${p.to}`}>
                <p.icon className="me-2 h-4 w-4 text-muted-foreground" />
                {p.label}
                <span className="ms-2 text-xs text-muted-foreground">{p.section}</span>
                <CornerDownLeft className="ms-auto h-3 w-3 text-muted-foreground/60" />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};
